/**
 * FocusModeProvider — V5.1+cockpit universal zoom-in stack
 * Source: pact/platform/focus-mode/focus-mode.md (Cluster S)
 * Plan: plan/ALIGNMENT-DELTAS.md Delta 12
 *
 * FreshCards supports 4 of the 20 constitutional element types:
 *   - card (via Peek)
 *   - page (full-screen Page Editor)
 *   - sub-view (full-screen Database sub-view)
 *   - property (zoomed property editor)
 *
 * Other 16 types are Studio-platform (AI reasoning, Code blocks, Diffs, Files, Chat messages, etc.) — out of scope.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type FocusElementType = 'card' | 'page' | 'sub-view' | 'property';
export type FocusElementId = string;

export interface FocusEntry {
  id: FocusElementId;
  type: FocusElementType;
  enteredAt: number;
}

interface FocusContextValue {
  stack: FocusEntry[];
  enter: (id: FocusElementId, type: FocusElementType) => void;
  exit: (id: FocusElementId) => void;
  exitAll: () => void;
  isFocused: (id: FocusElementId) => boolean;
  topOfStack: FocusEntry | null;
}

const FocusContext = createContext<FocusContextValue | null>(null);

export const FocusModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<FocusEntry[]>([]);

  const enter = useCallback((id: FocusElementId, type: FocusElementType) => {
    setStack((s) => {
      if (s.some((e) => e.id === id)) return s; // already focused
      return [...s, { id, type, enteredAt: Date.now() }];
    });
  }, []);

  const exit = useCallback((id: FocusElementId) => {
    setStack((s) => s.filter((e) => e.id !== id));
  }, []);

  const exitAll = useCallback(() => setStack([]), []);

  const value = useMemo<FocusContextValue>(
    () => ({
      stack,
      enter,
      exit,
      exitAll,
      isFocused: (id) => stack.some((e) => e.id === id),
      topOfStack: stack[stack.length - 1] ?? null,
    }),
    [stack, enter, exit, exitAll]
  );

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
};

export function useFocusMode(): FocusContextValue {
  const ctx = useContext(FocusContext);
  if (!ctx) throw new Error('useFocusMode must be used inside <FocusModeProvider>');
  return ctx;
}
